import { CommonModule } from "@angular/common";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { FUNCTION_RESULT_STATUS } from "@nova/common-interfaces";
import { IInvoice } from "@rfm/shared-interfaces";
import { ConfirmationService, MenuItem, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { MenuModule } from "primeng/menu";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { ToastModule } from "primeng/toast";
import { BoolToTextPipe } from "../../../../../../lib/pipes/bool-to-text.pipe.mjs";
import { JsonToTextPipe } from "../../../../../../lib/pipes/json-to-text.pipe.mjs";
import { InvoiceService } from "../../../../../shared/services/financial/invoice.service.mjs";

@Component({
  selector: "app-invoice-view",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ConfirmDialogModule,
    CardModule,
    ToastModule,
    ButtonModule,
    ProgressSpinnerModule,
    MenuModule,
    BoolToTextPipe,
    JsonToTextPipe,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: "./invoice-view.component.html",
  styleUrls: ["./invoice-view.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class InvoiceViewComponent implements OnInit {
  object: IInvoice | null = null; // Store the selected conduit details
  isLoading = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: InvoiceService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  items: MenuItem[] = [
    {
      label: "Edit",
      icon: "pi pi-file-edit",
      command: () => this.edit(),
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => this.delete(),
    },
    {
      label: "Re-instate",
      icon: "pi pi-check",
      command: () => this.reinstate(),
    },
    {
      label: "Mark Paid",
      icon: "pi pi-dollar",
      command: () => this.markPaid(),
    },
  ];

  async markPaid() {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: "Are you sure that you want to mark this invoice as paid?",
      header: "Mark Invoice Paid",
      closable: true,
      closeOnEscape: true,
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: "Cancel",
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: "Mark Invoice Paid",
        severity: "primary",
      },
      accept: async () => {
        this.isLoading = true;

        this.service
          .markPaid(this.object!.id)
          .then((result) => {
            if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Invoice marked as paid ....",
              });

              this.object = result.data!;
            } else {
              this.messageService.add({
                severity: "error",
                summary: "Error",
                detail: "Failed to mark invoice paid ....",
              });
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
    });
  }

  async edit() {
    this.router.navigate(['/admin/invoices/edit', this.object?.id])
  }

  async delete() {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: "Are you sure that you want to delete this invoice?",
      header: "Delete Invoice",
      closable: true,
      closeOnEscape: true,
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: "Cancel",
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: "Delete Invoice",
      },
      accept: async () => {
        this.isLoading = true;
        await this.service
          .delete(this.object!)
          .then((result) => {
            if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Invoice deleted successfully ..... redirecting",
              });
              this.router.navigate(["/admin/customers/view", this.object?.customer_id.id]);
            } else {
              this.messageService.add({
                severity: "error",
                summary: "Failed",
                detail: "Failed to delete the invoice",
              });
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
    });
  }

  async reinstate() {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: "Are you sure that you want to re-instate this invoice?",
      header: "Re-instate Invoice",
      closable: true,
      closeOnEscape: true,
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: "Cancel",
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: "Re-instate Invoice",
      },
      accept: async () => {
        this.isLoading = true;
        await this.service
          .reinstate(this.object!.id)
          .then((result) => {
            if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
              this.messageService.add({
                severity: "success",
                summary: "Success",
                detail: "Invoice re-instated successfully ..... redirecting",
              });
              this.router.navigate(["/admin/customers/view", this.object?.customer_id.id]);
            } else {
              this.messageService.add({
                severity: "error",
                summary: "Failed",
                detail: "Failed to re-instate the invoice",
              });
            }
          })
          .finally(() => {
            this.isLoading = false;
          });
      },
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get("id");
    if (id) {
      this.load(id);
    }
  }

  load(id: string): void {
    this.isLoading = true;
    this.service
      .get(id)
      .then((response) => {
        this.object = response?.data ?? null; // Handle null or undefined data
        this.isLoading = false;
      })
      .catch((error) => {
        console.error("Error fetching subscription details:", error);
        this.isLoading = false;
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}
